import React, { useState, useEffect } from 'react';
import MetricCard from './MetricCard';
import HeatMap from './HeatMap';
import DonutChart from './DonutChart';
import TrendLine from './TrendLine';
import { PieChart, Shield, Box } from 'lucide-react';
import api from '../utils/api';

// Helper function to format entity type names (from old dashboard)
const formatEntityTypeName = (name) => {
  const specialCases = {
    'DYNAMO_DB_TABLE': 'DynamoDB Table',
    'CLOUD_APPLICATION_NAMESPACE': 'Cloud Application',
    'CLOUD_APPLICATION_INSTANCE': 'Cloud Instance',
    'SERVICE_INSTANCE': 'Service Instance',
    'PROCESS_GROUP_INSTANCE': 'Process Group',
    'PROCESS_GROUP': 'Process Group'
  };
  
  if (specialCases[name]) {
    return specialCases[name];
  }
  
  // Convert UPPER_CASE to Title Case
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const Dashboard = () => {
  const [metricsData, setMetricsData] = useState([
    {
      id: 1,
      title: 'Software Changes',
      value: '94',
      icon: 'code',
      color: 'purple',
      chartType: 'line'
    },
    {
      id: 2,
      title: 'Monitored Services',
      value: '37',
      icon: 'server',
      color: 'cyan',
      chartType: 'bar'
    },
    {
      id: 3,
      title: 'Confidence Score',
      value: '48.7%',
      icon: 'chart',
      color: 'orange',
      chartType: 'area'
    },
    {
      id: 4,
      title: 'Change Health',
      value: '99.0%',
      icon: 'health',
      color: 'green',
      chartType: 'circle'
    },
    {
      id: 5,
      title: 'Monitored Elements',
      value: '278',
      icon: 'monitor',
      color: 'yellow',
      chartType: 'line'
    },
    {
      id: 6,
      title: 'Unique Entities',
      value: '7',
      icon: 'box',
      color: 'orange',
      chartType: 'bar'
    },
    {
      id: 7,
      title: 'Nodes',
      value: '37',
      icon: 'nodes',
      color: 'green',
      chartType: 'area'
    },
    {
      id: 8,
      title: 'ServiceNow to Dynatrace Matching Rate',
      value: '93.5%',
      icon: 'target',
      color: 'cyan',
      chartType: 'circle'
    }
  ]);

  const [stats, setStats] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch dashboard stats and records from old endpoints
        let stats = null;
        let recordsResponse = null;

        try {
          stats = await api.getDeploymentStats();
          console.log('[Dashboard] Stats loaded:', stats);
        } catch (statsError) {
          console.warn('[Dashboard] Could not fetch stats:', statsError);
        }

        try {
          recordsResponse = await api.getEnhancedDeployments(1000);
          console.log(`[Dashboard] Records loaded`);
        } catch (recordsError) {
          console.warn('[Dashboard] Could not fetch records:', recordsError);
        }

        // Transform to metrics format using API method
        if (stats || recordsResponse) {
          const transformed = api.transformDashboardData(stats, recordsResponse);
          if (transformed.metrics) {
            setMetricsData(transformed.metrics);
          }
          if (transformed.stats) {
            setStats(transformed.stats);
          }
        }

        // Fetch heatmap data
        try {
          const heatmap = await api.getDeploymentHeatmap(7);
          console.log('[Dashboard] Heatmap data received:', heatmap);
          if (heatmap && heatmap.heatmap) {
            console.log('[Dashboard] Heatmap has', heatmap.heatmap.length, 'data points');
          }
          setHeatmapData(heatmap);
        } catch (heatmapError) {
          console.warn('[Dashboard] Could not fetch heatmap data:', heatmapError);
        }
      } catch (error) {
        console.error('[Dashboard] Error fetching dashboard data:', error);
        console.log('[Dashboard] Using mock data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 relative">
      {/* Neon glow effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-accent opacity-[0.08] blur-[150px] rounded-full pointer-events-none animate-pulse" 
           style={{ animationDuration: '4s' }} />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-accent opacity-[0.06] blur-[150px] rounded-full pointer-events-none animate-pulse" 
           style={{ animationDuration: '5s', animationDelay: '1s' }} />
      <div className="absolute top-1/2 right-1/3 w-80 h-80 bg-orange-accent opacity-[0.05] blur-[140px] rounded-full pointer-events-none animate-pulse" 
           style={{ animationDuration: '6s', animationDelay: '2s' }} />
      
      {/* Header */}
      <div className="mb-4 sm:mb-6 relative z-10">
        <h2 className="text-xl sm:text-2xl font-bold text-cyan-accent mb-0.5 tracking-tight" 
            style={{ textShadow: '0 0 30px #00e5ff, 0 0 60px #00e5ff, 0 0 90px #00e5ff' }}>
          Dashboard
        </h2>
        <p className="text-xs sm:text-sm text-gray-500">Monitor your system's health and performance metrics</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 relative z-10 mb-6">
        {metricsData.map((metric) => (
          <MetricCard key={metric.id} {...metric} />
        ))}
      </div>

      {/* Heat Map and Trend Line Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 relative z-10 mb-6">
        <div className="lg:col-span-3">
          <HeatMap heatmapData={heatmapData} />
        </div>
        <div className="lg:col-span-1">
          <TrendLine />
        </div>
      </div>

      {/* Donut Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 relative z-10 mb-6">
        <DonutChart
          title="Service Health Status"
          icon={PieChart}
          data={stats?.current_state_breakdown && Object.keys(stats.current_state_breakdown).length > 0
            ? Object.entries(stats.current_state_breakdown).map(([state, count]) => ({
                category: state,
                value: count
              }))
            : [
                { category: "Move to Prod", value: 45 },
                { category: "In Progress", value: 32 },
                { category: "Completed", value: 18 },
                { category: "Pending", value: 12 }
              ]}
          categoryField="category"
          valueField="value"
        />
        
        <DonutChart
          title="Confidence Score Distribution"
          icon={Shield}
          data={stats?.match_status_breakdown && Object.keys(stats.match_status_breakdown).length > 0
            ? Object.entries(stats.match_status_breakdown).map(([status, count]) => ({
                category: status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                value: count
              }))
            : [
                { category: "Matched High Confidence", value: 45 },
                { category: "Matched Medium Confidence", value: 32 },
                { category: "Matched Low Confidence", value: 18 },
                { category: "Not Found", value: 12 }
              ]}
          categoryField="category"
          valueField="value"
        />
      </div>

      {/* Entity Types Chart - Full Width */}
      <div className="relative z-10">
        <DonutChart
          title="Entity Types"
          icon={Box}
          data={stats?.entity_type_breakdown && Object.keys(stats.entity_type_breakdown).length > 0
            ? Object.entries(stats.entity_type_breakdown).map(([type, count]) => ({
                category: formatEntityTypeName(type),
                value: count
              }))
            : [
                { category: "Service Instance", value: 125 },
                { category: "Process Group", value: 98 },
                { category: "Cloud Application", value: 76 },
                { category: "DynamoDB Table", value: 54 },
                { category: "Application", value: 43 },
                { category: "Host", value: 32 }
              ]}
          categoryField="category"
          valueField="value"
        />
      </div>
    </div>
  );
};

export default Dashboard;

